import { ConfigProvider, theme } from 'antd'
import { observer } from 'mobx-react-lite'
import { useLayoutEffect } from 'react'

import { AntdConfig } from '@/Ship/Configs/AntdConfig'
import { BrandConfig } from '@/Ship/Configs/BrandConfig'
import { themeManager } from '@/Ship/Stores/ThemeManager'

import type { ThemeMode } from '@/Ship/Stores/ThemeManager'
import type { ThemeConfig } from 'antd'
import type { ReactNode } from 'react'

import './theme.css'

/**
 * Те, чим теми відрізняються; решта — спільний `AntdConfig`.
 *
 * Світла — як була досі: рамки карток темніші за антдівські за замовчуванням.
 * Темній досить алгоритму: її кольори antd виводить сам із тих самих токенів.
 * Бренд зі своїми тлами темної теми (`BrandConfig.darkTokens`, ADR 048) кладе
 * їх сюди ж: від теми вони залежать, тож в `AntdConfig` їм не місце.
 */
const MODES: Record<ThemeMode, ThemeConfig> = {
    light: {
        algorithm: theme.defaultAlgorithm,
        components: { Card: { colorBorderSecondary: '#d9d9d9' } },
    },
    dark: {
        algorithm: theme.darkAlgorithm,
        ...(BrandConfig.darkTokens ? { token: { ...BrandConfig.darkTokens } } : {}),
    },
}

const STYLE_ID = 'wl-theme-tokens'

/** Спільний `AntdConfig` (бренд, шрифт, радіуси) разом із відмінностями теми. */
const fullTheme = (mode: ThemeMode): ThemeConfig => ({
    ...AntdConfig.theme,
    ...MODES[mode],
    token: { ...AntdConfig.theme?.token, ...MODES[mode].token },
})

/** `colorBgContainer` → `--ant-color-bg-container`: так змінні називає сам antd. */
const cssVarName = (token: string): string =>
    `--ant-${token}`
        .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
        .replace(/([A-Z]+)([A-Z][a-z0-9]+)/g, '$1-$2')
        .replace(/([a-z])([A-Z0-9])/g, '$1-$2')
        .toLowerCase()

/**
 * Кольори теми змінними на `:root`.
 *
 * Свої змінні antd оголошує лише на власних компонентах, тож `var(--ant-…)` у
 * нашому CSS поза ними значення не мав: на тлі сторінки, на екрані входу, у
 * календарі дат, що відкривається просто в `body`. Тут ті самі змінні з тими
 * самими значеннями діють на весь документ.
 */
const rootVariables = (mode: ThemeMode): string => {
    const tokens = theme.getDesignToken(fullTheme(mode)) as unknown as Record<string, unknown>

    const lines = Object.entries(tokens)
        .filter(
            ([name, value]) =>
                typeof value === 'string' &&
                (name.startsWith('color') || name.startsWith('boxShadow') || name === 'fontFamily')
        )
        .map(([name, value]) => `    ${cssVarName(name)}: ${String(value).replace(/\s+/g, ' ').trim()};`)

    return `:root {\n${lines.join('\n')}\n}`
}

// Статичні `Modal.confirm` і `message.*` малюються поза деревом React і
// провайдера не бачать, тож тему їм задаємо глобально — повну, з токенами
// `AntdConfig`: без них і в світлій лишався б синій antd замість кольору
// бренду. antd кличе цю функцію на кожен такий виклик — тема береться та, що
// чинна в ту мить
ConfigProvider.config({
    holderRender: (children) => <ConfigProvider theme={fullTheme(themeManager.mode)}>{children}</ConfigProvider>,
})

/**
 * Тема всього застосунку.
 *
 * Стоїть НАД ядровим `Layout`: його `ConfigProvider` читає `AntdConfig` один
 * раз і на зміни не зважає. Вкладений провайдер antd бере в батька алгоритм,
 * якого не задає сам, — тому алгоритму й кольорів, що залежать від теми, в
 * `AntdConfig` немає, їх перемикає цей провайдер.
 *
 * Тему віддаємо повну — разом із брендом і шрифтом `AntdConfig`, а не лише
 * відмінності режиму: лоадер застосунку (`DefaultLoadTemplate`) малюється ще
 * до ядрового `Layout`, і без цього його `Spin` крутився стандартним синім.
 */
export const ThemeProvider = observer(({ children }: { children: ReactNode }) => {
    const mode = themeManager.mode

    useLayoutEffect(() => {
        let style = document.getElementById(STYLE_ID)

        if (!style) {
            style = document.createElement('style')
            style.id = STYLE_ID
            document.head.appendChild(style)
        }

        style.textContent = rootVariables(mode)
    }, [mode])

    return <ConfigProvider theme={fullTheme(mode)}>{children}</ConfigProvider>
})
